// src/components/hooks/useKeyboardWedge.js
import { useEffect, useRef } from "react";

/**
 * useKeyboardWedge
 * Capture les codes-barres d'une douchette (mode "clavier wedge").
 *
 * Principes clés :
 *  - Détecte la douchette par la vélocité de frappe (< scannerSpeedMs entre 2 chars).
 *  - Utilise e.code (position physique) en priorité → indépendant du layout AZERTY/QWERTY.
 *  - Filtre tous les chars hors ASCII imprimable (0x21-0x7E) pour éviter les "codes bizarres"
 *    produits par l'interprétation AZERTY de certains HID keycodes.
 *  - state.lastTime est mis à jour pour TOUS les événements (même les chars rejetés)
 *    afin que la détection de vélocité reste fiable.
 *  - Bloque les chars dans les inputs dès le 1er char rapide détecté.
 */

// Mapping physique e.code → caractère ASCII standard
// Couvre chiffres, lettres et quelques ponctuations de barcode (-, ., /)
// Ce mapping est layout-indépendant : e.code = position physique, pas le char affiché
const PHYSICAL_MAP = {
  // Chiffres (rangée numérique)
  Digit0:'0', Digit1:'1', Digit2:'2', Digit3:'3', Digit4:'4',
  Digit5:'5', Digit6:'6', Digit7:'7', Digit8:'8', Digit9:'9',
  // Pavé numérique
  Numpad0:'0', Numpad1:'1', Numpad2:'2', Numpad3:'3', Numpad4:'4',
  Numpad5:'5', Numpad6:'6', Numpad7:'7', Numpad8:'8', Numpad9:'9',
  NumpadDecimal:'.', NumpadSubtract:'-', NumpadAdd:'+', NumpadMultiply:'*',
  // Lettres — même position physique sur AZERTY et QWERTY sauf exceptions gérées ci-dessous
  KeyA:'A', KeyB:'B', KeyC:'C', KeyD:'D', KeyE:'E', KeyF:'F',
  KeyG:'G', KeyH:'H', KeyI:'I', KeyJ:'J', KeyK:'K', KeyL:'L',
  KeyM:'M', KeyN:'N', KeyO:'O', KeyP:'P', KeyQ:'Q', KeyR:'R',
  KeyS:'S', KeyT:'T', KeyU:'U', KeyV:'V', KeyW:'W', KeyX:'X',
  KeyY:'Y', KeyZ:'Z',
  // Ponctuations communes dans les codes-barres (Code128, Code39)
  Minus:'-', Period:'.', Slash:'/', Space:' ',
};

// Fallback AZERTY : touche de chiffre pressée sans Shift → char AZERTY → chiffre
const AZERTY_DIGIT = {
  '&':'1', 'é':'2', '"':'3', "'": '4', '(':'5',
  '-':'6', 'è':'7', '_':'8', 'ç':'9', 'à':'0',
};

export default function useKeyboardWedge(
  onCode,
  { minChars = 6, endKey = "Enter", timeoutMs = 150, scannerSpeedMs = 60 } = {}
) {
  const onCodeRef = useRef(onCode);
  onCodeRef.current = onCode;

  const optsRef = useRef({ minChars, endKey, timeoutMs, scannerSpeedMs });
  optsRef.current = { minChars, endKey, timeoutMs, scannerSpeedMs };

  useEffect(() => {
    const state = {
      buf: "",
      lastTime: 0,
      fastCount: 0,
      timer: null,
      // Dès qu'on détecte que la séquence est une douchette, on bloque tous les chars suivants
      isScannerSeq: false,
    };

    const clearBuf = () => {
      clearTimeout(state.timer);
      state.timer = null;
      state.buf = "";
      state.fastCount = 0;
      state.lastTime = 0;
      state.isScannerSeq = false;
    };

    const flush = () => {
      const code = state.buf;
      clearBuf();
      const { minChars: min } = optsRef.current;
      if (code.length >= min && typeof onCodeRef.current === "function") {
        try { onCodeRef.current(code); } catch { /* no-op */ }
      }
    };

    const handler = (e) => {
      try {
        const { endKey: end, timeoutMs: tms, scannerSpeedMs: speed } = optsRef.current;
        const rawKey = typeof e?.key === "string" ? e.key : "";

        // Ignorer composition IME et raccourcis (Ctrl, Alt, Meta)
        if (e?.isComposing) return;
        if (e?.ctrlKey || e?.metaKey || e?.altKey) return;

        const now = Date.now();
        const elapsed = state.lastTime ? now - state.lastTime : Infinity;
        const isFast = elapsed < speed;

        // ── Touche de fin (Enter) ──
        if (rawKey === (end || "Enter") || e?.code === "Enter" || e?.code === "NumpadEnter") {
          if (state.buf.length >= optsRef.current.minChars) {
            e.preventDefault?.();
            e.stopImmediatePropagation?.();
            flush();
          } else {
            clearBuf();
          }
          return;
        }

        // ── Résolution du caractère : e.code (physique) en priorité ──
        let key = PHYSICAL_MAP[e?.code];

        if (key !== undefined) {
          // Pour les lettres, appliquer la casse selon Shift
          if (key >= 'A' && key <= 'Z' && !e.shiftKey) {
            key = key.toLowerCase();
          }
        } else {
          // Fallback : AZERTY digit map, puis rawKey direct
          key = AZERTY_DIGIT[rawKey] ?? rawKey;
        }

        // ── Mise à jour du timing pour TOUS les événements char ──
        // (même les chars qui seront rejetés — nécessaire pour que
        //  la détection de vélocité reste correcte entre deux chars ASCII)
        const isCharEvent = key && key.length === 1;
        if (isCharEvent) {
          // Marquer comme séquence scanner dès qu'un char est rapide
          if (isFast) {
            state.fastCount += 1;
            if (state.fastCount >= 1) state.isScannerSeq = true;
          }
          state.lastTime = now;
        }

        // ── Bloquer les chars dans les champs si c'est une séquence scanner ──
        if (state.isScannerSeq && isCharEvent) {
          const t = e?.target;
          const tag = t?.tagName ? String(t.tagName).toLowerCase() : "";
          if (tag === "input" || tag === "textarea" || t?.isContentEditable === true) {
            e.preventDefault?.();
          }
        }

        // ── Filtrer : n'accepter dans le buffer que l'ASCII imprimable (0x21-0x7E) ──
        const codePoint = key ? key.charCodeAt(0) : 0;
        const isAsciiPrintable = key && key.length === 1 && codePoint >= 0x21 && codePoint <= 0x7E;

        if (!isAsciiPrintable) {
          // Char non-ASCII ou non-imprimable : on a déjà mis à jour lastTime,
          // on n'ajoute rien au buffer
          if (!state.buf) clearBuf();
          return;
        }

        // ── Ajouter au buffer ──
        if (!isFast && !state.isScannerSeq) {
          // Frappe lente et pas encore une séquence scanner → reset
          if (state.buf.length !== 1) clearBuf();
        }

        state.buf += key;

        const flushDelay = state.buf.length === 1
          ? Math.max(600, Number(tms) || 600)
          : Math.max(40, Number(tms) || 150);
        clearTimeout(state.timer);
        state.timer = setTimeout(flush, flushDelay);

      } catch { /* avaler toute erreur */ }
    };

    document.addEventListener("keydown", handler, { passive: false });

    return () => {
      document.removeEventListener("keydown", handler);
      clearTimeout(state.timer);
    };
  }, []);
}
